import { z } from 'zod';

export const signUpSchema = z.object({
  name: z.string()
    .trim()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters')
    .regex(/^[a-zA-Z\s'-]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes'),
  
  email: z.string()
    .trim()
    .email('Invalid email address')
    .max(255, 'Email must be less than 255 characters'),
  
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
  
  phoneNumber: z.string()
    .optional()
    .refine((val) => !val || /^\+?[1-9]\d{1,14}$/.test(val), {
      message: 'Invalid phone number format (use E.164 format: +1234567890)'
    }),
  
  purpose: z.string()
    .max(500, 'Purpose must be less than 500 characters')
    .optional(),
  
  country: z.string()
    .max(100, 'Country must be less than 100 characters')
    .optional(),
  
  city: z.string()
    .max(100, 'City must be less than 100 characters')
    .optional(),
});

export const signInSchema = z.object({
  email: z.string().trim().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export const customerSchema = z.object({
  name: z.string()
    .trim()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters'),
  phone: z.string()
    .optional()
    .refine((val) => !val || /^\+?[0-9\s\-()]+$/.test(val), {
      message: 'Invalid phone number format'
    }),
  email: z.string()
    .optional()
    .refine((val) => !val || z.string().email().safeParse(val).success, {
      message: 'Invalid email address'
    }),
  address: z.string().max(500).optional(),
  opening_balance: z.number().default(0),
  balance_type: z.enum(['receivable', 'payable']).default('receivable'),
  notes: z.string().max(1000).optional(),
  group_id: z.string().optional(),
});

export const vendorSchema = z.object({
  name: z.string()
    .trim()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters'),
  company_name: z.string().max(100).optional(),
  phone: z.string()
    .optional()
    .refine((val) => !val || /^\+?[0-9\s\-()]+$/.test(val), {
      message: 'Invalid phone number format'
    }),
  email: z.string()
    .optional()
    .refine((val) => !val || z.string().email().safeParse(val).success, {
      message: 'Invalid email address'
    }),
  address: z.string().max(500).optional(),
  opening_balance: z.number().default(0),
  balance_type: z.enum(['receivable', 'payable']).default('payable'),
  notes: z.string().max(1000).optional(),
  group_id: z.string().optional(),
});

export const ledgerEntrySchema = z.object({
  transaction_type: z.enum(['sale', 'purchase', 'payment', 'receipt', 'adjustment']),
  date: z.string(),
  amount: z.number().positive('Amount must be positive'),
  description: z.string().max(500).optional(),
  reference: z.string().max(50).optional(),
});

export const invoiceItemSchema = z.object({
  product_id: z.string().optional(),
  product_name: z.string().min(1, 'Product name required'),
  quantity: z.number().int().positive('Quantity must be positive'),
  price: z.number().nonnegative('Price cannot be negative'),
});

export const invoiceSchema = z.object({
  invoice_number: z.string().min(1, 'Invoice number required'),
  date: z.string(),
  line_items: z.array(invoiceItemSchema).min(1, 'At least one item required'),
  tax: z.number().nonnegative().default(0),
  discount: z.number().nonnegative().default(0),
  paid_amount: z.number().nonnegative().default(0),
  notes: z.string().max(1000).optional(),
});

export const stockAdjustmentSchema = z.object({
  product_id: z.string().min(1, 'Product is required'),
  adjustment_type: z.enum(['manual', 'damage', 'found', 'customer_return', 'vendor_return']),
  quantity_change: z.number().int('Must be a whole number'),
  reason: z.string().min(5, 'Reason must be at least 5 characters').max(500),
});
